/* eslint-disable react-hooks/exhaustive-deps */
import { XGrid, XGridToolbar } from '@AMIEWEB/Common';
import { gridSelectionActions } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.redux';
import { selectGridSelection } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.selector';
import {
  selectCreatedUnitFromOrder,
  selectUnitsData,
  selectUnitsGridData,
  selectUnitsLoading,
} from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.selector';
import { selectActiveStep, selectNewOrderCreatedFromUnit, selectUnitId } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import SearchIcon from '@mui/icons-material/Search';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { InputAdornment, TextField } from '@mui/material';
import { GridTag } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getFormattedRows, unitColumnsNewOrderCreation } from './utils';
import { OrderCreationStepEnum } from '@AMIEWEB/NewOrder/types';
import { FacilityUnitActions } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.redux';

const useStyles = makeStyles()(theme => ({
  textField: {
    margin: '-7px -10px -3px 0px',
    '&::placeholder': {
      opacity: 0.6,
      color: theme.palette.system.neutralGray,
    },
  },
}));

export const UnitList = () => {
  const loading = useSelector(selectUnitsLoading);
  const apiRef = useGridApiRef();
  const unitData = useSelector(selectUnitsData);
  const unitsFlattenedData = useSelector(selectUnitsGridData);
  const newUnitFromOrder = useSelector(selectCreatedUnitFromOrder);
  const selectedData = useSelector(selectGridSelection)?.selectedData;
  const activeStep = useSelector(selectActiveStep);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const columns = unitColumnsNewOrderCreation();
  const { classes } = useStyles();
  const selectedUnitId = useSelector(selectUnitId);
  const isNewOrderCreatedFromUnit = useSelector(selectNewOrderCreatedFromUnit);


  /**
   * Method to set units grid data
   */
  const setUnitGridData = (searchText?: string) => {
    let filteredRows = unitsFlattenedData;
    if (searchText && searchText !== '') {
      filteredRows = unitsFlattenedData?.filter(unit => {
        return unit?.name?.toLowerCase()?.indexOf(searchText.toLowerCase()) !== -1;
      });
    }
    const formattedRows = getFormattedRows(filteredRows);
    setRows(formattedRows);
    setTotalCount(formattedRows.length ? formattedRows.length : 0);
  };

  /**
   * Effect to setup grid data for unitsF
   */
  useEffect(() => {
    if (unitsFlattenedData?.length > 0) setUnitGridData();
  }, [unitsFlattenedData]);

  /**
   * Effect to setup selected unit
   */
  useEffect(() => {
    if (selectedData.length > 0 && activeStep === OrderCreationStepEnum.UnitSelection && !isNewOrderCreatedFromUnit) {
      dispatch(orderCreationNewActions.setSelectedUnitId(selectedData[0]));
      dispatch(orderCreationNewActions.getSkillsetForSelectedUnitAction({ unitId: selectedData[0] }));
      unitData.filter(data =>
        data.unitId === parseInt(selectedData[0]) ? dispatch(orderCreationNewActions.setSelectedUnitItem(data)) : null,
      );
    }
  }, [selectedData]);

  useEffect(() => {
    let timeoutId;
    if (unitsFlattenedData?.length > 0 && newUnitFromOrder &&  !isNewOrderCreatedFromUnit) {
      dispatch(orderCreationNewActions.setSelectedUnitId(unitsFlattenedData[unitsFlattenedData.length - 1].unitId));
      dispatch(gridSelectionActions.setGridSelections({ selectedData: [selectedUnitId] }));

      timeoutId = setTimeout(() => {
        dispatch(FacilityUnitActions.setCreateNewUnitFromOrder(false));
        dispatch(FacilityUnitActions.setCreateNewUnit(false));
      }, 500);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [newUnitFromOrder, unitsFlattenedData, dispatch, selectedUnitId]);

  const Search = (e, text) => {
    setSearchValue(text);
    if (text !== '') setUnitGridData(text);
    else setUnitGridData();
  };
  const SearchTextfieldRenderer = () => () => {
    return (
      <TextField
        id="searchBox"
        variant="outlined"
        size="small"
        disabled={unitsFlattenedData.length === 0}
        value={searchValue}
        onChange={e => Search(e, e.target.value)}
        className={classes.textField}
        placeholder={t('order.newOrderCreation.unitCreation.searchUnitName')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{ width: '18px', height: '18px', color: theme => theme.palette.system.neutralGray, opacity: 0.6 }}
              />
            </InputAdornment>
          ),
        }}
      />
    );
  };
  const RightActions = React.useMemo(() => {
    return [
      {
        renderer: SearchTextfieldRenderer(),
        rightDivider: false,
        leftDivider: false,
      },
    ];
  }, [searchValue, unitsFlattenedData]);

  const UnitsGrid = useMemo(
    () => (
      <XGrid
        sx={{ minHeight: '200px !important' }}
        gridTag={GridTag.FacilityUnitsGrid}
        apiRef={apiRef}
        loading={loading}
        columns={columns}
        rows={rows}
        height={200}
        disableSelectionOnClick
        checkboxSelection
        rowCount={totalCount}
        toolbar
        toolbarProps={{
          rightActions: RightActions,
          columnSelector: false,
          exportButton: false,
          moreOption: false,
          rightDivider: false,
        }}
        components={{
          Toolbar: XGridToolbar,
        }}
        selectionModel={[selectedUnitId]}
        hideFooter
        title={t('facility.units.unitContacts.gridHeaders.units')}
        disableColumnMenu
        disableColumnFilter
        disableDensitySelector
        pagination={false}
      />
    ),
    [loading, columns, rows, searchValue, totalCount, unitsFlattenedData],
  );

  return <>{UnitsGrid}</>;
};
